import React, { useState, useEffect, useMemo } from 'react';
import { 
  getAdminOrders, 
  createAdminManualOrder, 
  updateAdminOrderStatus, 
  updateAdminOrderPaymentStatus, 
  deleteAdminOrder, 
  approveAdminTransferOrder 
} from '../services/apiService';
import '../styles/adminpage.css';

const ORDER_SOURCES = [
  { value: 'WEB', label: 'Web Sitesi', color: '#3b82f6' },
  { value: 'INSTAGRAM', label: 'Instagram', color: '#a855f7' },
  { value: 'WHATSAPP', label: 'WhatsApp', color: '#22c55e' }
];

const ORDER_STATUSES = [
  { value: 'RECEIVED', label: 'Sipariş Alındı', color: '#6b7280' },
  { value: 'DESIGNING', label: 'Tasarım Aşamasında', color: '#f59e0b' },
  { value: 'PRINTING', label: 'Baskıda', color: '#3b82f6' },
  { value: 'SHIPPED', label: 'Kargoya Verildi', color: '#8b5cf6' },
  { value: 'COMPLETED', label: 'Tamamlandı', color: '#10b981' },
  { value: 'CANCELLED', label: 'İptal Edildi', color: '#ef4444' }
];

const PAYMENT_METHODS = [
  { value: 'CREDIT_CARD', label: 'Kredi Kartı' },
  { value: 'WIRE_TRANSFER', label: 'Havale/EFT' },
  { value: 'CASH', label: 'Elden / Nakit' }
];

const PAYMENT_STATUSES = [
  { value: 'PENDING', label: 'Ödeme Bekliyor' },
  { value: 'PENDING_TRANSFER', label: 'Havale Bekliyor' },
  { value: 'COMPLETED', label: 'Ödendi' },
  { value: 'FAILED', label: 'Başarısız' }
];

const PACKAGES = [
  { id: 'dugun-ozel', name: 'Sadece Qr Kod', price: 1200, freeMasa: 0, freeEl: 0 },
  { id: 'dugun-baslangic', name: 'Başlangıç Düğün', price: 1350, freeMasa: 15, freeEl: 24 },
  { id: 'dugun-standart', name: 'Standart Düğün', price: 1900, freeMasa: 50, freeEl: 40 },
  { id: 'dugun-pro', name: 'Pro Düğün', price: 3950, freeMasa: 120, freeEl: 120, freePano: 1 },
  { id: 'ekonomik-1', name: 'Ekonomik Paket 1', price: 990, freeMasa: 25, freeEl: 0 },
  { id: 'ekonomik-2', name: 'Ekonomik Paket 2', price: 1250, freeMasa: 50, freeEl: 0 },
  { id: 'ekonomik-3', name: 'Ekonomik Paket 3', price: 1600, freeMasa: 100, freeEl: 0 },
  { id: 'ekonomik-4', name: 'Ekonomik Paket 4', price: 1900, freeMasa: 150, freeEl: 0 },
  { id: 'ekonomik-5', name: 'Ekonomik Paket 5', price: 2150, freeMasa: 200, freeEl: 0 },
  { id: 'custom', name: 'Özel / Diğer', price: 0, freeMasa: 0, freeEl: 0 }
];

const MASA_KARTI_PRICE = 12;
const EL_KARTI_PRICE = 5;
const PANO_PRICE = 900;

const getDaysRemaining = (eventDateStr) => {
  if (!eventDateStr) return null;
  const eventDate = new Date(eventDateStr);
  if (isNaN(eventDate.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  eventDate.setHours(0, 0, 0, 0);
  const diffTime = eventDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export default function AdminOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtreler
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Manuel Sipariş Ekleme Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    cift: '',
    email: '',
    phone: '',
    eventDate: '',
    address: '',
    city: '',
    town: '',
    postalCode: '',
    note: '',
    packageName: PACKAGES[0].name,
    packagePrice: PACKAGES[0].price,
    extraMasa: 0,
    extraEl: 0,
    extraPano: 0,
    orderSource: 'INSTAGRAM',
    paymentMethod: 'WIRE_TRANSFER',
    paymentStatus: 'PENDING',
    orderStatus: 'RECEIVED'
  });

  useEffect(() => {
    fetchOrders();
  }, [filterSource, filterStatus]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterSource) filters.source = filterSource;
      if (filterStatus) filters.status = filterStatus;
      if (search.trim()) filters.search = search.trim();
      
      const data = await getAdminOrders(filters);
      setOrders(data);
    } catch (err) {
      alert('Siparişler yüklenemedi: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchOrders();
  };

  const handleResetFilters = () => {
    setSearch('');
    setFilterSource('');
    setFilterStatus('');
  };

  // Inline Güncellemeler
  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateAdminOrderStatus(orderId, newStatus);
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, orderStatus: newStatus } : o));
    } catch (err) {
      alert('Sipariş durumu güncellenemedi: ' + err.message);
    }
  };

  const handlePaymentStatusChange = async (orderId, newPaymentStatus) => {
    try {
      await updateAdminOrderPaymentStatus(orderId, newPaymentStatus);
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, paymentStatus: newPaymentStatus } : o));
    } catch (err) {
      alert('Ödeme durumu güncellenemedi: ' + err.message);
    }
  };

  const handleApproveHavale = async (orderId) => {
    if (!window.confirm("Havalenin geldiğini onaylıyor musunuz?")) return;
    try {
      await approveAdminTransferOrder(orderId);
      alert('Havale başarıyla onaylandı.');
      fetchOrders();
    } catch (err) {
      alert('Onay hatası: ' + err.message);
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Bu siparişi kalıcı olarak silmek istediğinize emin misiniz? (Bu işlem geri alınamaz)")) return;
    try {
      await deleteAdminOrder(orderId);
      alert('Sipariş başarıyla silindi.');
      fetchOrders();
    } catch (err) {
      alert('Sipariş silinemedi: ' + err.message);
    }
  };

  // Form Değişiklik Yönetimi
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Eğer paket değiştiyse varsayılan fiyatını otomatik ata
      if (name === 'packageName') {
        const pkg = PACKAGES.find(p => p.name === value);
        if (pkg) {
          updated.packagePrice = pkg.price;
        }
      }
      return updated;
    });
  };

  // Fiyat Hesaplama
  const calculatedTotal = useMemo(() => {
    const pkgPrice = Number(formData.packagePrice) || 0;
    const extraMasaTotal = (Number(formData.extraMasa) || 0) * MASA_KARTI_PRICE;
    const extraElTotal = (Number(formData.extraEl) || 0) * EL_KARTI_PRICE;
    const extraPanoTotal = (Number(formData.extraPano) || 0) * PANO_PRICE;
    return pkgPrice + extraMasaTotal + extraElTotal + extraPanoTotal;
  }, [formData.packagePrice, formData.extraMasa, formData.extraEl, formData.extraPano]);

  const handleAddManualOrder = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const items = [];
      const selectedPkg = PACKAGES.find(p => p.name === formData.packageName) || PACKAGES[0];
      
      if (Number(formData.extraMasa) > 0 || selectedPkg.freeMasa > 0) {
        items.push({
          name: 'Masa Kartı',
          quantity: (Number(formData.extraMasa) || 0) + (selectedPkg.freeMasa || 0),
          freeQty: selectedPkg.freeMasa || 0,
          unitPrice: MASA_KARTI_PRICE,
          subtotal: (Number(formData.extraMasa) || 0) * MASA_KARTI_PRICE
        });
      }
      if (Number(formData.extraEl) > 0 || selectedPkg.freeEl > 0) {
        items.push({
          name: 'El Kartı',
          quantity: (Number(formData.extraEl) || 0) + (selectedPkg.freeEl || 0),
          freeQty: selectedPkg.freeEl || 0,
          unitPrice: EL_KARTI_PRICE,
          subtotal: (Number(formData.extraEl) || 0) * EL_KARTI_PRICE
        });
      }
      if (Number(formData.extraPano) > 0 || selectedPkg.freePano > 0) {
        items.push({
          name: 'Karşılama Panosu',
          quantity: (Number(formData.extraPano) || 0) + (selectedPkg.freePano || 0),
          freeQty: selectedPkg.freePano || 0,
          unitPrice: PANO_PRICE,
          subtotal: (Number(formData.extraPano) || 0) * PANO_PRICE
        });
      }

      const payload = {
        shippingDetails: {
          name: formData.name,
          cift: formData.cift,
          email: formData.email || 'manual@anora.net',
          phone: formData.phone,
          eventDate: formData.eventDate,
          address: formData.address,
          city: formData.city,
          town: formData.town,
          postalCode: formData.postalCode,
          note: formData.note
        },
        packageName: formData.packageName,
        packagePrice: Number(formData.packagePrice),
        items,
        total: calculatedTotal,
        orderSource: formData.orderSource,
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentStatus,
        orderStatus: formData.orderStatus
      };

      await createAdminManualOrder(payload);
      alert('Manuel sipariş başarıyla eklendi.');
      setIsModalOpen(false);
      
      // Formu sıfırla
      setFormData({
        name: '',
        cift: '',
        email: '',
        phone: '',
        eventDate: '',
        address: '',
        city: '',
        town: '',
        postalCode: '',
        note: '',
        packageName: PACKAGES[0].name,
        packagePrice: PACKAGES[0].price,
        extraMasa: 0,
        extraEl: 0,
        extraPano: 0,
        orderSource: 'INSTAGRAM',
        paymentMethod: 'WIRE_TRANSFER',
        paymentStatus: 'PENDING',
        orderStatus: 'RECEIVED'
      });

      fetchOrders();
    } catch (err) {
      alert('Sipariş eklenirken hata: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ marginTop: '20px' }}>
      
      {/* Üst İşlemler & Filtreler */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        
        {/* Arama ve Filtreleme Formu */}
        <form onSubmit={handleSearchSubmit} style={{ 
          display: 'flex', 
          gap: '10px', 
          flexWrap: 'wrap',
          alignItems: 'center' 
        }}>
          <input 
            type="text" 
            placeholder="Müşteri adı, çift veya tel..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '220px', padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          
          <select 
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
          >
            <option value="">Tüm Kaynaklar</option>
            {ORDER_SOURCES.map(src => (
              <option key={src.value} value={src.value}>{src.label}</option>
            ))}
          </select>

          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
          >
            <option value="">Tüm Durumlar</option>
            {ORDER_STATUSES.map(st => (
              <option key={st.value} value={st.value}>{st.label}</option>
            ))}
          </select>

          <button type="submit" className="btn btn-sm">Ara</button>
          
          {(search || filterSource || filterStatus) && (
            <button 
              type="button" 
              className="btn-outline btn-sm"
              onClick={handleResetFilters}
            >
              Filtreleri Temizle
            </button>
          )}
        </form>

        <button 
          className="btn" 
          onClick={() => setIsModalOpen(true)}
          style={{ backgroundColor: '#a855f7', color: 'white' }}
        >
          + Manuel Sipariş Ekle
        </button>
      </div>

      {/* Sipariş Listesi */}
      {loading ? (
        <div>Siparişler yükleniyor...</div>
      ) : (
        <div className="table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>Tarih</th>
                <th style={{ width: '110px' }}>Kaynak</th>
                <th>Müşteri / Teslimat Bilgileri</th>
                <th>Sipariş Detayı</th>
                <th style={{ width: '180px' }}>Ödeme</th>
                <th style={{ width: '160px' }}>Sipariş Durumu</th>
                <th style={{ width: '120px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>Sipariş bulunamadı.</td></tr>
              ) : (
                orders.map(order => {
                  const srcObj = ORDER_SOURCES.find(s => s.value === order.orderSource) || ORDER_SOURCES[0];
                  
                  return (
                    <tr key={order._id}>
                      {/* Tarih */}
                      <td style={{ fontSize: '0.85rem' }}>
                        {new Date(order.createdAt).toLocaleDateString('tr-TR')}<br />
                        <small className="text-gray-400">
                          {new Date(order.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </small>
                      </td>

                      {/* Sipariş Kaynağı */}
                      <td>
                        <span 
                          className="badge" 
                          style={{ backgroundColor: srcObj.color, color: 'white', fontWeight: 'bold' }}
                        >
                          {srcObj.label}
                        </span>
                      </td>

                      {/* Müşteri / Teslimat */}
                      <td style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                        <strong>{order.shippingDetails?.name}</strong> <br />
                        <span style={{ color: '#ec4899', fontWeight: 'bold' }}>💍 {order.shippingDetails?.cift}</span> <br />
                        <small className="text-gray-500">📞 {order.shippingDetails?.phone}</small>
                        {order.shippingDetails?.email && (
                          <>
                            <br />
                            <small className="text-gray-500">✉️ {order.shippingDetails.email}</small>
                          </>
                        )}
                        <br />
                        <small className="text-gray-600">
                          📍 {order.shippingDetails?.address}, {order.shippingDetails?.town}/{order.shippingDetails?.city}
                        </small>
                        {order.shippingDetails?.eventDate && (
                          <div style={{ marginTop: '3px' }}>
                            <small className="text-gray-600 font-semibold">📅 Etkinlik: {new Date(order.shippingDetails.eventDate).toLocaleDateString('tr-TR')}</small>
                            {(() => {
                              const days = getDaysRemaining(order.shippingDetails.eventDate);
                              if (days === null) return null;
                              return (
                                <span style={{ 
                                  marginLeft: '6px', 
                                  fontSize: '0.75rem', 
                                  fontWeight: 'bold', 
                                  color: days < 0 ? '#ef4444' : days === 0 ? '#22c55e' : '#2563eb' 
                                }}>
                                  {days < 0 ? `(${Math.abs(days)} gün geçti)` : days === 0 ? '(Bugün!)' : `(${days} gün kaldı)`}
                                </span>
                              );
                            })()}
                          </div>
                        )}
                        {order.shippingDetails?.note && (
                          <div style={{ 
                            marginTop: '5px', 
                            fontSize: '0.75rem', 
                            backgroundColor: '#fef3c7', 
                            padding: '3px 6px', 
                            borderRadius: '3px',
                            color: '#d97706'
                          }}>
                            <strong>Not:</strong> {order.shippingDetails.note}
                          </div>
                        )}
                      </td>

                      {/* Sipariş Detayı */}
                      <td style={{ fontSize: '0.85rem' }}>
                        <strong className="text-purple-700">{order.packageName}</strong>
                        <div style={{ marginTop: '5px', color: '#555' }}>
                          {order.items?.map((item, idx) => (
                            <div key={idx}>
                              • {item.name}: <strong>{item.quantity}</strong> adet 
                              {item.freeQty > 0 && <small className="text-green-600"> ({item.freeQty} ücretsiz)</small>}
                            </div>
                          ))}
                        </div>
                      </td>

                      {/* Ödeme */}
                      <td>
                        <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '5px' }}>
                          ₺{order.total.toLocaleString('tr-TR')}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '5px' }}>
                          {PAYMENT_METHODS.find(m => m.value === order.paymentMethod)?.label || order.paymentMethod}
                        </div>
                        
                        {/* Ödeme Durumu Dropdown */}
                        <select 
                          value={order.paymentStatus}
                          onChange={(e) => handlePaymentStatusChange(order._id, e.target.value)}
                          style={{ 
                            fontSize: '0.75rem', 
                            padding: '2px 4px', 
                            border: '1px solid #ccc', 
                            borderRadius: '3px',
                            backgroundColor: order.paymentStatus === 'COMPLETED' ? '#d1fae5' : '#fee2e2',
                            color: order.paymentStatus === 'COMPLETED' ? '#065f46' : '#991b1b',
                            fontWeight: 'bold'
                          }}
                        >
                          {PAYMENT_STATUSES.map(ps => (
                            <option key={ps.value} value={ps.value}>{ps.label}</option>
                          ))}
                        </select>
                      </td>

                      {/* Sipariş Durumu */}
                      <td>
                        <select 
                          value={order.orderStatus || 'RECEIVED'}
                          onChange={(e) => handleStatusChange(order._id, e.target.value)}
                          style={{ 
                            fontSize: '0.8rem', 
                            padding: '4px 8px', 
                            border: '1px solid #ccc', 
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            backgroundColor: '#f9fafb',
                            width: '100%'
                          }}
                        >
                          {ORDER_STATUSES.map(os => (
                            <option key={os.value} value={os.value}>{os.label}</option>
                          ))}
                        </select>
                      </td>

                      {/* İşlemler */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          {order.paymentStatus === 'PENDING_TRANSFER' && (
                            <button 
                              className="btn btn-xs" 
                              onClick={() => handleApproveHavale(order._id)}
                              style={{ backgroundColor: '#10b981', color: 'white' }}
                            >
                              Havale Onayla
                            </button>
                          )}
                          <button 
                            className="btn-danger btn-xs" 
                            onClick={() => handleDelete(order._id)}
                          >
                            Siparişi Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Manuel Sipariş Ekleme Modalı */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <button onClick={() => setIsModalOpen(false)} className="modal-close-btn">
              &times;
            </button>
            
            <form onSubmit={handleAddManualOrder}>
              <h2 style={{ marginBottom: '20px', color: '#a855f7' }}>Manuel Sipariş Ekle (Dış Kanal)</h2>
              
              <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '5px', marginBottom: '15px', color: '#374151' }}>
                1. Müşteri & Çift Bilgileri
              </h3>
              
              <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Müşteri Adı / Soyadı *</label>
                  <input 
                    type="text" 
                    name="name" 
                    placeholder="örn: Ahmet Yılmaz" 
                    value={formData.name} 
                    onChange={handleFormChange} 
                    required 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Çift İsimleri *</label>
                  <input 
                    type="text" 
                    name="cift" 
                    placeholder="Selin & Ozan" 
                    value={formData.cift} 
                    onChange={handleFormChange} 
                    required 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Telefon Numarası *</label>
                  <input 
                    type="text" 
                    name="phone" 
                    placeholder="05..." 
                    value={formData.phone} 
                    onChange={handleFormChange} 
                    required 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
              </div>

              <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>E-Posta (İsteğe Bağlı)</label>
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="ornek@mail.com" 
                    value={formData.email} 
                    onChange={handleFormChange} 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Düğün Tarihi</label>
                  <input 
                    type="date" 
                    name="eventDate" 
                    value={formData.eventDate} 
                    onChange={handleFormChange} 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Sipariş Kaynağı *</label>
                  <select 
                    name="orderSource" 
                    value={formData.orderSource} 
                    onChange={handleFormChange}
                    style={{ width: '100%', padding: '6px' }}
                  >
                    <option value="INSTAGRAM">Instagram</option>
                    <option value="WHATSAPP">WhatsApp</option>
                  </select>
                </div>
              </div>

              <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '5px', marginBottom: '15px', marginTop: '25px', color: '#374151' }}>
                2. Paket & Ürün Detayları
              </h3>

              <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '15px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Paket Seçimi *</label>
                  <select 
                    name="packageName" 
                    value={formData.packageName} 
                    onChange={handleFormChange}
                    style={{ width: '100%', padding: '6px' }}
                  >
                    {PACKAGES.map(p => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Paket Fiyatı (₺)</label>
                  <input 
                    type="number" 
                    name="packagePrice" 
                    value={formData.packagePrice} 
                    onChange={handleFormChange} 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Ek Masa Kartı (+14₺)</label>
                  <input 
                    type="number" 
                    name="extraMasa" 
                    min="0" 
                    value={formData.extraMasa} 
                    onChange={handleFormChange} 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Ek El Kartı (+5₺)</label>
                  <input 
                    type="number" 
                    name="extraEl" 
                    min="0" 
                    value={formData.extraEl} 
                    onChange={handleFormChange} 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Ek Karşılama Panosu (+900₺)</label>
                  <input 
                    type="number" 
                    name="extraPano" 
                    min="0" 
                    value={formData.extraPano} 
                    onChange={handleFormChange} 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
              </div>

              <h3 style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '5px', marginBottom: '15px', marginTop: '25px', color: '#374151' }}>
                3. Teslimat Adresi & Ödeme
              </h3>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Adres Detayı *</label>
                <input 
                  type="text" 
                  name="address" 
                  placeholder="Mahalle, cadde, no..." 
                  value={formData.address} 
                  onChange={handleFormChange} 
                  required 
                  style={{ width: '100%', padding: '6px' }}
                />
              </div>

              <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>İlçe / Semt *</label>
                  <input 
                    type="text" 
                    name="town" 
                    placeholder="Kadıköy" 
                    value={formData.town} 
                    onChange={handleFormChange} 
                    required 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Şehir *</label>
                  <input 
                    type="text" 
                    name="city" 
                    placeholder="İstanbul" 
                    value={formData.city} 
                    onChange={handleFormChange} 
                    required 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Posta Kodu *</label>
                  <input 
                    type="text" 
                    name="postalCode" 
                    placeholder="34710" 
                    value={formData.postalCode} 
                    onChange={handleFormChange} 
                    required 
                    style={{ width: '100%', padding: '6px' }}
                  />
                </div>
              </div>

              <div className="grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Ödeme Yöntemi</label>
                  <select 
                    name="paymentMethod" 
                    value={formData.paymentMethod} 
                    onChange={handleFormChange}
                    style={{ width: '100%', padding: '6px' }}
                  >
                    {PAYMENT_METHODS.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Ödeme Durumu</label>
                  <select 
                    name="paymentStatus" 
                    value={formData.paymentStatus} 
                    onChange={handleFormChange}
                    style={{ width: '100%', padding: '6px' }}
                  >
                    <option value="PENDING">Ödeme Bekliyor</option>
                    <option value="COMPLETED">Ödendi</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Sipariş Durumu</label>
                  <select 
                    name="orderStatus" 
                    value={formData.orderStatus} 
                    onChange={handleFormChange}
                    style={{ width: '100%', padding: '6px' }}
                  >
                    {ORDER_STATUSES.map(os => (
                      <option key={os.value} value={os.value}>{os.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Sipariş Notu</label>
                <textarea 
                  name="note" 
                  placeholder="Kargo notu veya ekstra tasarım notları..." 
                  value={formData.note} 
                  onChange={handleFormChange} 
                  style={{ width: '100%', padding: '6px', height: '60px' }}
                />
              </div>

              {/* Toplam Fiyat Bilgisi */}
              <div style={{ 
                backgroundColor: '#f3e8ff', 
                padding: '15px', 
                borderRadius: '8px', 
                textAlign: 'right',
                marginBottom: '20px',
                border: '1px dashed #c084fc'
              }}>
                <span style={{ fontSize: '1rem', color: '#5b21b6', fontWeight: 'medium' }}>Hesaplanan Toplam Tutar: </span>
                <strong style={{ fontSize: '1.5rem', color: '#5b21b6' }}>₺{calculatedTotal.toLocaleString('tr-TR')}</strong>
              </div>

              <button 
                type="submit" 
                className="btn" 
                disabled={isSubmitting} 
                style={{ width: '100%', padding: '12px', backgroundColor: '#a855f7', color: 'white', fontSize: '1rem' }}
              >
                {isSubmitting ? 'Sipariş Kaydediliyor...' : 'Manuel Siparişi Ekle'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
